import { Link, NavLink } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { topics } from '../../data/topics';
import logo from '../../static/logo.png';

const Header = () => {
  const navItems = useMemo(() => (
    Object.entries(topics).map(([, topic]) => ({
      name: topic.title.replace(' Questions', ''),
      path: topic.path,
    }))
  ), []);

  const utilityItems = useMemo(() => ([
    { name: 'Mock Interview', path: '/mock-interview'},
  ]), []);

  const [mobileOpen, setMobileOpen] = useState(false);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <header className="sticky top-0 z-50 shadow-lg transition-shadow duration-300 hover:shadow-xl" style={{ backgroundColor: '#2b572a' }}>
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex items-center justify-between gap-3 sm:gap-4">
          {/* Left: Utility Links (hidden on mobile) */}
          <div className="hidden sm:flex gap-2">
            {utilityItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg font-medium text-sm sm:text-base transition-all duration-300 whitespace-nowrap ${
                  isActive
                    ? 'bg-white/20 text-white shadow-lg scale-105'
                    : 'text-white hover:bg-white/20 hover:scale-105 hover:shadow-lg active:scale-95'
                }`}
              >
                <span className="hidden lg:inline">{item.name}</span>
              </NavLink>
            ))}
          </div>

          {/* Center: Logo */}
          <Link
            to="/"
            className="flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
          >
            <img
              src={logo}
              alt="The Web Dev Interview"
              className="h-20 sm:h-24 transition-transform duration-300"
            />
          </Link>

          {/* Right: Navigation Topics (hidden on mobile) */}
          <nav className="hidden sm:flex flex-wrap gap-2 justify-end py-1" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `px-2 sm:px-3 py-2 rounded-lg font-medium text-sm sm:text-base transition-all duration-300 whitespace-nowrap ${
                  isActive
                    ? 'bg-white/20 text-white shadow-lg scale-105'
                    : 'text-white hover:bg-white/20 hover:scale-105 hover:shadow-lg active:scale-95'
                }`}
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/10 text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95"
            aria-label="Open menu"
            aria-controls="mobile-menu"
            aria-expanded={mobileOpen}
            onClick={openMobile}
          >
            <span className="sr-only">Open menu</span>
            <span aria-hidden="true" className="text-xl">☰</span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileOpen && (
        <div className="sm:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="mobile-menu-title" id="mobile-menu">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 animate-fade-in"
            onClick={closeMobile}
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 h-full w-72 max-w-full bg-white shadow-2xl animate-slide-in-right">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="font-bold text-gray-800 text-lg" id="mobile-menu-title">Menu</span>
              <button
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:bg-gray-100 transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="Close menu"
                onClick={closeMobile}
              >
                <span aria-hidden="true" className="text-lg">✕</span>
              </button>
            </div>
            <div className="px-4 py-3 space-y-2">
              {/* Utility */}
              {utilityItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-200 hover:translate-x-1 active:scale-95 text-base"
                >
                  <span>{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
              <div className="border-t my-2" />
              {/* Topics */}
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={closeMobile}
                  className={({ isActive }) => `block px-3 py-2 rounded-lg font-medium transition-all duration-200 hover:translate-x-1 active:scale-95 text-base ${
                    isActive ? 'bg-orange-50 text-orange-600 shadow-sm' : 'text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
